// https://fluentsite.z22.web.core.windows.net/quick-start
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  tokens,
} from "@fluentui/react-components";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HashRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { app } from "@microsoft/teams-js";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { FlexdeskBookings } from "./FlexdeskBookings";
import { Tickets } from "./Tickets";
import { Messages } from "./Messages";
import { TeamsFxContext } from "./Context";
import config from "./sample/lib/config";
import { Inventories } from "./Inventories";
import { Settings } from "./Settings";
import { useToken } from "../utils/useToken";

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
const App = () => {
  const { i18n } = useTranslation();
  const { loading, theme, themeString, teamsUserCredential, context } = useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint,
    clientId: config.clientId,
  });
  useToken();

  const teamsValues = useMemo(
    () => ({ theme, themeString, teamsUserCredential }),
    [theme, themeString, teamsUserCredential]
  );

  const fluentProviderTheme = useMemo(() => {
    switch (themeString) {
      case "dark":
        return teamsDarkTheme;
      case "contrast":
        return teamsHighContrastTheme;
      default:
        return {
          ...teamsLightTheme,
          colorNeutralBackground3: "#eeeeee",
        };
    }
  }, [themeString]);

  useEffect(() => {
    const i18nextLng = localStorage.getItem("i18nextLng");
    if (context?.app?.locale) {
      const isDeBrowserLng = context.app.locale.includes("de-");
      if (
        (i18nextLng.includes("en") && isDeBrowserLng) ||
        (i18nextLng === "de-DE" && !isDeBrowserLng)
      ) {
        i18n.changeLanguage(isDeBrowserLng ? "de-DE" : "en");
      }
    }
  }, [i18n, context?.app?.locale]);

  useEffect(() => {
    loading &&
      app.initialize().then(() => {
        // Hide the loading indicator.
        app.notifySuccess();
      });
  }, [loading]);
  return (
    <TeamsFxContext.Provider value={teamsValues}>
      <FluentProvider
        theme={fluentProviderTheme}
        style={{ background: tokens.colorNeutralBackground3 }}
      >
        <Router>
          {!loading && (
            <Routes>
              <Route path="/flexdesk-bookings-tab" element={<FlexdeskBookings />} />
              <Route path="/tickets-tab" element={<Tickets />} />
              <Route path="/messages-tab" element={<Messages />} />
              <Route path="/inventories-tab" element={<Inventories />} />
              <Route path="/settings-tab" element={<Settings />} />
              <Route path="*" element={<Navigate to="/flexdesk-bookings-tab" />} />
            </Routes>
          )}
        </Router>
      </FluentProvider>
    </TeamsFxContext.Provider>
  );
};

export default App;
